/**
 * #2197 — mandatory application documents, client half.
 *
 * Which slots are mandatory is a staff setting (Settings → Applications),
 * read from GET /api/applications/document-requirements. The server refuses
 * the submit on BOTH routes (`/applications/:id/submit`, `/renewals/:id/submit`)
 * when one is empty, so the client block here is the courtesy that tells the
 * family before they reach the consent box — never the gate itself.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { presentDocumentKinds } from '@/lib/documents';

/** The react-query key the wizard and the staff settings tab share, so a
 *  staff change invalidates both. */
export const DOCUMENT_REQUIREMENTS_QUERY_KEY = ['applications', 'document-requirements'] as const;

const NONE: readonly string[] = Object.freeze([]);

/**
 * The mandatory kinds. While loading, or if the read fails, this is EMPTY:
 * the server is the authority and still refuses a submit with the missing
 * documents named, so a failed read can delay the warning but never let a
 * family past the gate — and it cannot strand them behind a requirement the
 * staff setting no longer has.
 */
export function useRequiredDocumentKinds(): readonly string[] {
  const query = useQuery({
    queryKey: DOCUMENT_REQUIREMENTS_QUERY_KEY,
    queryFn: async () => (await api.applications.documentRequirements()).data?.required ?? [],
    staleTime: 10_000,
  });
  return query.data ?? NONE;
}

/** Flag the slots TRF has made mandatory. Kept apart from `buildDocSlots`
 *  so the copy editor's preview (which calls that builder) renders the plain
 *  tiles, and the slot-cap parity contract keeps reading one array literal. */
export function markRequiredSlots<S extends { kind: string; required?: boolean }>(
  slots: readonly S[],
  required: readonly string[],
): S[] {
  return slots.map((slot) => (required.includes(slot.kind) ? { ...slot, required: true } : slot));
}

/** #2472 legacy rule: two family photos also fill the child-photo slot. The
 *  one client predicate lives in `@/lib/documents` (`presentDocumentKinds`),
 *  the mirror of the server's `backend/src/helpers/childPhotoSlot.js`, so the
 *  wizard and the staff review page cannot disagree. Re-exported here for the
 *  wizard's existing callers. */
export { LEGACY_FAMILY_PHOTOS_FOR_CHILD_SLOT } from '@/lib/documents';

/** The required slots that have no active upload yet, in tile order. #2472:
 *  one upload fills a slot (the child's photo and the family's are two slots),
 *  the same rule as the server's `missingRequiredDocuments()`, legacy rule
 *  included. */
export function missingRequiredSlots<S extends { kind: string; required?: boolean }>(
  slots: readonly S[],
  uploaded: readonly { kind: string }[],
): S[] {
  const present = presentDocumentKinds(uploaded).kinds;
  return slots.filter((s) => s.required && !present.has(s.kind));
}
